const ICD10_CODE_PATTERN = /\b([A-TV-Z][0-9][0-9A-Z](?:\.[0-9A-Z]+)?)\b/i;
const ICD10_LINE_PATTERN = /^(?:code:\s*)?([A-TV-Z][0-9][0-9A-Z](?:\.[0-9A-Z]+)?)\s*(?:[-:–—]\s*)?(.+)?$/i;

const STOPWORDS = new Set([
  "the",
  "and",
  "for",
  "with",
  "from",
  "that",
  "this",
  "into",
  "when",
  "what",
  "more",
  "than",
  "have",
  "will",
  "then",
  "they",
  "their",
  "does",
  "were",
  "been",
  "was",
  "are",
  "not",
  "code",
  "codes",
  "icd",
  "diagnosis",
  "diagnoses",
  "unspecified",
]);

const safeString = (value) => (value == null ? "" : String(value));

const normalizeWhitespace = (value) => safeString(value).replace(/\s+/g, " ").trim();

const normalizeIcdCode = (value) => {
  const normalized = normalizeWhitespace(value).toUpperCase();

  if (normalized === "NO_ICD") {
    return "NO_ICD";
  }

  const exactMatch = normalized.match(/^([A-TV-Z][0-9][0-9A-Z](?:\.[0-9A-Z]+)?)$/i);

  if (exactMatch) {
    return exactMatch[1].toUpperCase();
  }

  const embeddedMatch = normalized.match(ICD10_CODE_PATTERN);
  return embeddedMatch ? embeddedMatch[1].toUpperCase() : "";
};

export const normalizeText = (value) => normalizeWhitespace(value).toLowerCase();

const uniqueBy = (items, keyFn) => {
  const seen = new Set();
  const next = [];

  items.forEach((item) => {
    const key = keyFn(item);
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    next.push(item);
  });

  return next;
};

const tokenize = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9.]+/g, " ")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token && !STOPWORDS.has(token) && token.length > 2);

const getBillingTextSource = (billingSource) => {
  if (!billingSource) {
    return "";
  }

  if (typeof billingSource === "string") {
    return billingSource;
  }

  return safeString(billingSource?.data?.billing_codes || billingSource?.data?.engine_v1_gpt || "");
};

const getOrdersWithDiagnosis = (billingSource) => {
  const candidates =
    billingSource?.data?.orders_with_diagnosis ||
    billingSource?.orders_with_diagnosis ||
    [];

  return Array.isArray(candidates) ? candidates : [];
};

const getIcdCodeValue = (value) => {
  if (typeof value === "string" || typeof value === "number") {
    return normalizeIcdCode(value);
  }

  if (value && typeof value === "object") {
    return normalizeIcdCode(
      value.code ||
        value.icd_code ||
        value.icd10_code ||
        value.icd ||
        value.icd10 ||
        value.diagnosis_code
    );
  }

  return "";
};

const getIcdDescriptionValue = (value) => {
  if (!value || typeof value !== "object") {
    if (typeof value === "string") {
      const matched = normalizeWhitespace(value).match(ICD10_LINE_PATTERN);

      if (matched?.[2]) {
        return normalizeWhitespace(matched[2]).replace(/^[-:–—]\s*/, "");
      }
    }

    if (typeof value === "string" && !normalizeIcdCode(value)) {
      return normalizeWhitespace(value);
    }

    return "";
  }

  const description = normalizeWhitespace(value.description || value.label || value.name || value.display || "");
  const code = getIcdCodeValue(value);
  const rawCode = normalizeWhitespace(
    value.code ||
      value.icd_code ||
      value.icd10_code ||
      value.icd ||
      value.icd10 ||
      value.diagnosis_code ||
      ""
  );

  if (description) {
    return description;
  }

  return rawCode && !code ? rawCode : "";
};

const getIcdDisplayNameValue = (value) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  return normalizeWhitespace(value.display_name || value.displayName || value.display || "");
};

const expandIcdCodeInput = (codes) => {
  if (!codes) {
    return [];
  }

  if (Array.isArray(codes)) {
    return codes;
  }

  if (typeof codes === "string") {
    const normalized = normalizeWhitespace(codes);

    if (!ICD10_CODE_PATTERN.test(normalized)) {
      return normalized ? [normalized] : [];
    }

    return codes.split(/[;\n]/g).flatMap((chunk) => {
      const normalizedChunk = normalizeWhitespace(chunk);

      if (!normalizedChunk) {
        return [];
      }

      if (!ICD10_CODE_PATTERN.test(normalizedChunk)) {
        return [normalizedChunk];
      }

      const commaParts = normalizedChunk.split(/,/g).map((value) => value.trim()).filter(Boolean);
      const everyPartHasCode = commaParts.length > 1 && commaParts.every((part) => ICD10_CODE_PATTERN.test(part));

      return everyPartHasCode ? commaParts : [normalizedChunk];
    });
  }

  return [codes];
};

export const normalizeIcdCodes = (codes) =>
  expandIcdCodeInput(codes)
    .map((value) => {
      const code = getIcdCodeValue(value);
      const description = getIcdDescriptionValue(value);
      const displayName = getIcdDisplayNameValue(value);

      if (!code && !description && !displayName) {
        return null;
      }

      return {
        code,
        description,
        display_name: displayName,
        label: code && description ? `${code} - ${description}` : description || displayName || code,
      };
    })
    .filter(Boolean);

export const dedupeIcdCodes = (codes) => {
  const merged = new Map();

  normalizeIcdCodes(codes).forEach((item) => {
    const normalizedKey =
      normalizeIcdCode(item.code) ||
      normalizeText(item.description || item.display_name || item.label);

    if (!normalizedKey) {
      return;
    }

    const current = merged.get(normalizedKey);
    if (!current) {
      merged.set(normalizedKey, {
        ...item,
        label: item.code && item.description ? `${item.code} - ${item.description}` : item.description || item.display_name || item.code,
      });
      return;
    }

    merged.set(normalizedKey, {
      ...current,
      ...item,
      description: current.description || item.description || "",
      display_name: current.display_name || item.display_name || "",
      label:
        item.code && item.description
          ? `${item.code} - ${item.description}`
          : current.label || item.description || item.display_name || item.code,
    });
  });

  return Array.from(merged.values());
};

const getOrderIcdInputs = (order = {}) => [
  order.icd_codes,
  order.icd10_codes,
  order.diagnosis_codes,
  order.icd_code_details,
  order.icd10_code_details,
  order.icd,
  order.icd_code,
  order.icd10,
  order.diagnosis_code,
  order.diagnosisCode,
  order.icdCode,
  order.code,
];

const findMatchingBillingOrder = (order = {}, ordersWithDiagnosis = []) => {
  const orderClinicalIntent = normalizeText(order.clinical_intent);
  const orderSelectedName = normalizeText(order.selected_order_name);

  if (orderClinicalIntent) {
    const clinicalMatch = ordersWithDiagnosis.find(
      (billingOrder) => normalizeText(billingOrder?.clinical_intent) === orderClinicalIntent
    );

    if (clinicalMatch) {
      return clinicalMatch;
    }
  }

  if (!orderSelectedName) {
    return null;
  }

  return (
    ordersWithDiagnosis.find(
      (billingOrder) => normalizeText(billingOrder?.selected_order_name) === orderSelectedName
    ) || null
  );
};

export const mapLinkedIcdCodesToOrders = (orders = [], ordersWithDiagnosis = []) => {
  if (!Array.isArray(orders)) {
    return [];
  }

  const billingOrders = Array.isArray(ordersWithDiagnosis) ? ordersWithDiagnosis : [];

  return orders.map((order = {}) => {
    const matchingBillingOrder = findMatchingBillingOrder(order, billingOrders);
    const existingCodes = dedupeIcdCodes(getOrderIcdInputs(order).flatMap(expandIcdCodeInput));
    const linkedCodes = dedupeIcdCodes(matchingBillingOrder?.linked_icd_codes);
    const mergedCodes = dedupeIcdCodes([...existingCodes, ...linkedCodes]);

    return {
      ...order,
      snomed_code: order.snomed_code || matchingBillingOrder?.snomed_code,
      icd_codes: mergedCodes.map((item) => item.code).filter(Boolean),
      icd_code_details: mergedCodes,
    };
  });
};

const getCodeDescriptionFromLine = (line) => {
  const cleanedLine = normalizeWhitespace(line.replace(/^[-*•]\s*/, ""));
  const matched = cleanedLine.match(ICD10_LINE_PATTERN);

  if (!matched) {
    return null;
  }

  const code = normalizeIcdCode(matched[1]);
  const description = normalizeWhitespace(matched[2] || "").replace(/^[-:–—]\s*/, "");

  if (!code) {
    return null;
  }

  return {
    code,
    description,
    label: description ? `${code} - ${description}` : code,
  };
};

export const parseBillingIcd10Codes = (billingSource) => {
  const text = getBillingTextSource(billingSource);

  if (!normalizeWhitespace(text)) {
    return [];
  }

  const lines = text.split(/\r?\n/);
  const parsed = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = normalizeWhitespace(lines[index]);

    if (!line) {
      continue;
    }

    const direct = getCodeDescriptionFromLine(line);

    if (direct) {
      parsed.push(direct);
      continue;
    }

    const codeOnlyMatch = line.match(/^[-*•]?\s*([A-TV-Z][0-9][0-9A-Z](?:\.[0-9A-Z]+)?)\s*$/i);

    if (!codeOnlyMatch) {
      continue;
    }

    let description = "";

    for (let lookahead = index + 1; lookahead < lines.length; lookahead += 1) {
      const nextLine = normalizeWhitespace(lines[lookahead]);

      if (!nextLine) {
        continue;
      }

      if (ICD10_LINE_PATTERN.test(nextLine) || (/^[-*•]/.test(nextLine) && ICD10_CODE_PATTERN.test(nextLine))) {
        break;
      }

      description = nextLine.replace(/^[-*•]\s*/, "");
      break;
    }

    parsed.push({
      code: normalizeIcdCode(codeOnlyMatch[1]),
      description,
      label: description ? `${normalizeIcdCode(codeOnlyMatch[1])} - ${description}` : normalizeIcdCode(codeOnlyMatch[1]),
    });
  }

  return uniqueBy(parsed, (item) => item.code);
};

const extractOrderCodeCandidates = (order = {}) => {
  const candidates = [];

  getOrderIcdInputs(order).forEach((raw) => {
    candidates.push(...expandIcdCodeInput(raw));
  });

  if (typeof order.diagnosis === "string" && order.diagnosis.trim()) {
    candidates.push(order.diagnosis);
  }

  return uniqueBy(
    candidates.map((value) => getIcdCodeValue(value)).filter(Boolean),
    (value) => value
  );
};

const extractOrderIcdDetails = (order = {}) => {
  const candidates = [];

  getOrderIcdInputs(order).forEach((raw) => {
    candidates.push(...expandIcdCodeInput(raw));
  });

  if (typeof order.diagnosis === "string" && order.diagnosis.trim()) {
    candidates.push(order.diagnosis);
  }

  return dedupeIcdCodes(candidates);
};

export const getOrderIdentityKey = (order = {}, index = 0) =>
  [
    normalizeText(order.selected_order_name || order.clinical_intent || order.name),
    normalizeText(order.order_type),
    normalizeText(order.priority),
    normalizeText(order.additional_instructions || order.notes),
    normalizeText(order.snomed_code || order.snomedCode),
    normalizeText(order.athena_order_id || order.order_id),
    normalizeText(
      order.icd_codes ||
        order.icd10_codes ||
        order.diagnosis_codes ||
        order.icd ||
        order.icd_code ||
        order.icd10 ||
        order.diagnosis_code ||
        order.diagnosisCode ||
        order.icdCode ||
        order.code
    ),
    index,
  ].join("|");

const getOrderSearchText = (order = {}) =>
  normalizeText(
    [
      order.selected_order_name,
      order.clinical_intent,
      order.name,
      order.order_type,
      order.priority,
      order.additional_instructions,
      order.notes,
      order.diagnosis,
      order.reason,
      order.assessment,
      order.plan,
      order.content,
    ]
      .filter(Boolean)
      .join(" ")
  );

const getSoapContextText = (soapNotes = {}) => {
  if (typeof soapNotes === "string") {
    return normalizeText(soapNotes);
  }

  return normalizeText(
    [
      soapNotes?.patient,
      soapNotes?.subjective?.chief_complaint,
      soapNotes?.subjective?.hpi,
      soapNotes?.subjective?.family_history,
      soapNotes?.subjective?.surgical_history,
      soapNotes?.subjective?.social_history,
      soapNotes?.subjective?.ros,
      ...(Array.isArray(soapNotes?.assessmentAndPlan?.problems)
        ? soapNotes.assessmentAndPlan.problems.flatMap((problem) => [
            problem?.problem,
            problem?.assessment,
            problem?.plan,
          ])
        : []),
      soapNotes?.assessmentAndPlan?.follow_up,
    ]
      .filter(Boolean)
      .join(" ")
  );
};

const scoreIcdMatch = ({ code, description }, orderText, soapContextText) => {
  let score = 0;
  const normalizedCode = normalizeText(code);
  const normalizedDescription = normalizeText(description);

  if (!normalizedCode) {
    return 0;
  }

  const exactCodePattern = new RegExp(`(^|[^a-z0-9])${normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`, "i");

  if (exactCodePattern.test(orderText)) {
    score += 120;
  }

  if (exactCodePattern.test(soapContextText)) {
    score += 40;
  }

  if (normalizedDescription) {
    if (orderText.includes(normalizedDescription)) {
      score += 80;
    }

    if (soapContextText.includes(normalizedDescription)) {
      score += 50;
    }

    const tokens = tokenize(normalizedDescription);
    tokens.forEach((token) => {
      if (orderText.includes(token)) {
        score += 6;
      }

      if (soapContextText.includes(token)) {
        score += 4;
      }
    });
  }

  return score;
};

const buildFallbackGroups = (mappedOrders) => {
  const groups = new Map();

  mappedOrders.forEach((item) => {
    if (item.icdDetails.length === 0) {
      const key = "NO_ICD";
      if (!groups.has(key)) {
        groups.set(key, {
          code: key,
          description: "No ICD assigned",
          label: "No ICD assigned",
          relatedOrders: [],
        });
      }
      groups.get(key).relatedOrders.push(item);
      return;
    }

    item.icdDetails.forEach((icd) => {
      const key = icd.code || normalizeText(icd.description || icd.display_name || icd.label);

      if (!key) {
        return;
      }

      if (!groups.has(key)) {
        groups.set(key, {
          ...icd,
          relatedOrders: [],
        });
      }

      groups.get(key).relatedOrders.push(item);
    });
  });

  return Array.from(groups.values());
};

export const buildIcdOrderViewModel = ({
  orders = [],
  billingSource,
  soapNotes,
} = {}) => {
  const billingIcdCodes = parseBillingIcd10Codes(billingSource);
  const billingCodeMap = new Map(billingIcdCodes.map((item) => [item.code, item]));
  const linkedOrders = mapLinkedIcdCodesToOrders(orders, getOrdersWithDiagnosis(billingSource));
  const soapContextText = getSoapContextText(soapNotes);

  const mappedOrders = linkedOrders.map((order, index) => {
    const orderKey = order?.__persistKey || getOrderIdentityKey(order, index);
    const existingCodes = extractOrderCodeCandidates(order);
    const existingIcdDetails = extractOrderIcdDetails(order);
    const orderText = `${getOrderSearchText(order)} ${soapContextText}`.trim();

    const scoredMatches = billingIcdCodes
      .map((entry) => ({
        ...entry,
        score: scoreIcdMatch(entry, orderText, soapContextText),
      }))
      .filter((entry) => entry.score > 0)
      .sort((left, right) => right.score - left.score || left.code.localeCompare(right.code));

    const resolvedCodes = [];
    const pushCode = (code) => {
      const normalized = normalizeIcdCode(code);
      if (!normalized || resolvedCodes.includes(normalized)) {
        return;
      }

      resolvedCodes.push(normalized);
    };

    existingCodes.forEach(pushCode);

    const icdDetails = resolvedCodes.map((code) => {
      const known = billingCodeMap.get(code);
      const existingDetail = existingIcdDetails.find((item) => item.code === code);

      if (known) {
        return {
          ...known,
          description: known.description || existingDetail?.description || "",
          label:
            known.description || existingDetail?.description
              ? `${code} - ${known.description || existingDetail?.description}`
              : code,
        };
      }

      if (existingDetail) {
        return existingDetail;
      }

      return {
        code,
        description: "",
        label: code,
      };
    });
    const codedDiagnosisSignatures = new Set(
      icdDetails
        .map((item) => normalizeDiagnosisSignature(item.description || item.display_name || item.label))
        .filter(Boolean)
    );
    const descriptionOnlyDetails = uniqueBy(
      existingIcdDetails.filter((item) => {
        if (item.code || !(item.description || item.display_name || item.label)) {
          return false;
        }

        const signature = normalizeDiagnosisSignature(item.description || item.display_name || item.label);
        return signature && !codedDiagnosisSignatures.has(signature);
      }),
      (item) => normalizeText(item.description || item.display_name || item.label)
    );
    const displayIcdDetails = [...icdDetails, ...descriptionOnlyDetails];

    return {
      order,
      orderKey,
      existingCodes,
      icdCodes: resolvedCodes,
      icdDetails: displayIcdDetails,
      primaryIcd: displayIcdDetails[0] || null,
      matchedBillingCodes: scoredMatches,
    };
  });

  const getDiagnosisGroupKey = (diagnosis = {}) =>
    diagnosis.code || normalizeText(diagnosis.description || diagnosis.display_name || diagnosis.label);

  const groupedCodes = uniqueBy(
    mappedOrders.flatMap((item) => item.icdDetails),
    getDiagnosisGroupKey
  );

  const diagnosisGroups = groupedCodes.length > 0
    ? groupedCodes.map((diagnosis) => ({
        ...diagnosis,
        relatedOrders: mappedOrders.filter((item) => {
          const diagnosisKey = getDiagnosisGroupKey(diagnosis);
          return (
            (diagnosis.code && item.icdCodes.includes(diagnosis.code)) ||
            item.icdDetails.some((icd) => getDiagnosisGroupKey(icd) === diagnosisKey)
          );
        }),
      }))
    : buildFallbackGroups(mappedOrders);

  return {
    billingIcdCodes,
    mappedOrders,
    diagnosisGroups,
  };
};

export const extractOrdersFromSoapResponse = (soapResponse) => {
  const structuredOrders = soapResponse?.data?.orders;

  if (Array.isArray(structuredOrders)) {
    return structuredOrders;
  }

  const rawSoap = safeString(soapResponse?.data?.soap_notes);

  if (!rawSoap.includes("$orders -")) {
    return [];
  }

  const afterOrders = rawSoap.split("$orders -")[1] || "";

  try {
    const cleaned = afterOrders
      .trim()
      .replace(/'/g, '"')
      .replace(/None/g, "null")
      .replace(/True/g, "true")
      .replace(/False/g, "false");

    const parsed = JSON.parse(cleaned.replace(/[^\x20-\x7E\n\r\t]/g, ""));

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (Array.isArray(parsed?.orders)) {
      return parsed.orders;
    }
  } catch {
    return [];
  }

  return [];
};

const normalizeDisplayName = (value) => normalizeWhitespace(value);

const normalizeDiagnosisSignature = (value = "") => {
  const normalized = safeString(value)
    .replace(/^ICD-10:\s*/i, "")
    .replace(/^[A-Z]\d+(?:\.\d+)?\s*-\s*/i, "")
    .replace(/\s*[-–—]\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim();

  const beforeSeparator = normalized.split(/\s+-\s+/)[0] || normalized;
  return beforeSeparator
    .replace(/^[.,\s]+|[.,\s]+$/g, "")
    .replace(/[.]{2,}/g, ".")
    .toLowerCase();
};

const shouldIncludeDisplayName = (description, displayName) => {
  const normalizedDescription = normalizeWhitespace(description || "");
  const normalizedDisplayName = normalizeWhitespace(displayName || "");

  if (!normalizedDisplayName) {
    return false;
  }

  if (!normalizedDescription) {
    return true;
  }

  return normalizedDescription.toLowerCase() !== normalizedDisplayName.toLowerCase();
};

const getPreferredIcdDisplayValue = (icd = {}) => {
  const code = normalizeIcdCode(icd?.code || icd?.icd_code || icd?.icd10_code || icd?.icd || icd?.icd10 || icd?.diagnosis_code);
  const description = normalizeWhitespace(icd?.description || icd?.label || icd?.name || "");
  const displayName = normalizeDisplayName(icd?.display_name || icd?.displayName || "");

  if (code === "NO_ICD") {
    return "Not linked";
  }

  if (!code) {
    const compact = (description || displayName).replace(/[.]{2,}/g, ".").replace(/[.]+$/g, "").trim();
    return compact ? `${compact}.` : "Not linked";
  }

  if (description) {
    const compact = description.replace(/[.]{2,}/g, ".").replace(/[.]+$/g, "").trim();
    if (shouldIncludeDisplayName(description, displayName)) {
      const compactDisplayName = displayName.replace(/[.]{2,}/g, ".").replace(/[.]+$/g, "").trim();
      return `${code} - ${compact}. (${compactDisplayName})`;
    }
    return `${code} - ${compact}.`;
  }

  if (displayName) {
    const compact = displayName.replace(/[.]{2,}/g, ".").replace(/[.]+$/g, "").trim();
    return `${code} - ${compact}.`;
  }

  return code;
};

const getCandidatePreferenceScore = (item = {}) => {
  const displayName = normalizeDisplayName(item?.display_name || item?.displayName || "");
  const description = normalizeWhitespace(item?.description || item?.label || item?.name || "");
  const code = normalizeIcdCode(item?.code || item?.icd_code || item?.icd10_code || item?.icd || item?.icd10 || item?.diagnosis_code);

  let score = 0;
  if (displayName) {
    score += 100;
  }
  if (description && description.toUpperCase() === description) {
    score += 25;
  }
  if (displayName && displayName.toUpperCase() === displayName) {
    score += 25;
  }
  if (code && !/^[A-Z]\d+(?:\.\d+)?$/i.test(code)) {
    score += 10;
  }
  return score;
};

export const formatIcdDisplayLine = (icd = {}) => {
  const code = normalizeIcdCode(icd?.code || icd?.icd_code || icd?.icd10_code || icd?.icd || icd?.icd10 || icd?.diagnosis_code);
  const description = normalizeWhitespace(icd?.description || icd?.label || icd?.name || "");
  const displayName = normalizeDisplayName(icd?.display_name || icd?.displayName || "");

  if (code === "NO_ICD" || (!code && !description && !displayName)) {
    return "ICD-10: Not linked";
  }

  const preferredValue = getPreferredIcdDisplayValue(icd).replace(/[.]{2,}/g, ".").replace(/\s+/g, " ").trim();
  const cleanedValue = preferredValue.replace(/[.]+$/g, ".");

  if (description || displayName) {
    return `ICD-10: ${cleanedValue}`;
  }

  return `ICD-10: ${code}`;
};

export const formatIcdDisplayLines = (codes = []) => {
  if (!Array.isArray(codes) || codes.length === 0) {
    return [formatIcdDisplayLine(null)];
  }

  const bySignature = new Map();

  codes.forEach((item) => {
    const line = formatIcdDisplayLine(item);
    const signature = normalizeDiagnosisSignature(line);

    if (!signature) {
      return;
    }

    const score = getCandidatePreferenceScore(item);
    const existing = bySignature.get(signature);

    if (!existing || score > existing.score) {
      bySignature.set(signature, { line, score });
    }
  });

  const formatted = Array.from(bySignature.values()).map((entry) => entry.line);
  return formatted.length > 0 ? formatted : [formatIcdDisplayLine(null)];
};

export const getIcdDisplayLabel = (icd = {}) => {
  const code = normalizeIcdCode(icd.code);
  const description = normalizeWhitespace(icd.description);
  if (!code || code === "NO_ICD") {
    return "-";
  }

  return description ? `${code} - ${description}` : code;
};
